import React, { useState, useRef, useEffect } from 'react';
import './ImageCropper.css';

const ImageCropper = ({ image, onCrop, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (image && imageRef.current) {
      const img = imageRef.current;
      img.onload = () => {
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
        // Set initial crop to center of image
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;
        const scaleX = img.naturalWidth / containerWidth;
        const scaleY = img.naturalHeight / containerHeight;
        
        setCrop({
          x: (containerWidth - 200) / 2,
          y: (containerHeight - 200) / 2,
          width: 200,
          height: 200
        });
      };
    }
  }, [image]);

  const handleMouseDown = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking on resize handles
    const handleSize = 10;
    const handles = {
      'nw': { x: crop.x, y: crop.y },
      'ne': { x: crop.x + crop.width - handleSize, y: crop.y },
      'sw': { x: crop.x, y: crop.y + crop.height - handleSize },
      'se': { x: crop.x + crop.width - handleSize, y: crop.y + crop.height - handleSize }
    };

    for (const [handle, pos] of Object.entries(handles)) {
      if (x >= pos.x && x <= pos.x + handleSize && y >= pos.y && y <= pos.y + handleSize) {
        setIsResizing(true);
        setResizeHandle(handle);
        setDragStart({ x, y });
        return;
      }
    }

    // Check if clicking inside crop area
    if (x >= crop.x && x <= crop.x + crop.width && y >= crop.y && y <= crop.y + crop.height) {
      setIsDragging(true);
      setDragStart({ x: x - crop.x, y: y - crop.y });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging && !isResizing) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (isDragging) {
      const newX = Math.max(0, Math.min(rect.width - crop.width, x - dragStart.x));
      const newY = Math.max(0, Math.min(rect.height - crop.height, y - dragStart.y));
      setCrop(prev => ({ ...prev, x: newX, y: newY }));
    } else if (isResizing) {
      let newCrop = { ...crop };
      
      switch (resizeHandle) {
        case 'nw':
          newCrop.width = Math.max(50, crop.x + crop.width - x);
          newCrop.height = Math.max(50, crop.y + crop.height - y);
          newCrop.x = x;
          newCrop.y = y;
          break;
        case 'ne':
          newCrop.width = Math.max(50, x - crop.x);
          newCrop.height = Math.max(50, crop.y + crop.height - y);
          newCrop.y = y;
          break;
        case 'sw':
          newCrop.width = Math.max(50, crop.x + crop.width - x);
          newCrop.height = Math.max(50, y - crop.y);
          newCrop.x = x;
          break;
        case 'se':
          newCrop.width = Math.max(50, x - crop.x);
          newCrop.height = Math.max(50, y - crop.y);
          break;
      }
      
      setCrop(newCrop);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  const handleCrop = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;
    
    // Calculate actual crop dimensions based on image scale
    const scaleX = img.naturalWidth / img.clientWidth;
    const scaleY = img.naturalHeight / img.clientHeight;
    
    const actualCrop = {
      x: crop.x * scaleX,
      y: crop.y * scaleY,
      width: crop.width * scaleX,
      height: crop.height * scaleY
    };
    
    canvas.width = actualCrop.width;
    canvas.height = actualCrop.height;
    
    ctx.drawImage(
      img,
      actualCrop.x, actualCrop.y, actualCrop.width, actualCrop.height,
      0, 0, actualCrop.width, actualCrop.height
    );
    
    canvas.toBlob((blob) => {
      const croppedFile = new File([blob], image.name, { type: image.type });
      onCrop(croppedFile);
    }, image.type);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, crop, dragStart, resizeHandle]);

  if (!image) return null;

  return (
    <div className="image-cropper-overlay">
      <div className="image-cropper-container">
        <div className="image-cropper-header">
          <h3>Crop Image</h3>
          <div className="image-cropper-controls">
            <button onClick={handleCrop} className="crop-btn">
              Crop & Upload
            </button>
            <button onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
        
        <div 
          className="image-cropper-area"
          ref={containerRef}
          onMouseDown={handleMouseDown}
        >
          <img
            ref={imageRef}
            src={URL.createObjectURL(image)}
            alt="Crop preview"
            className="crop-preview-image"
          />
          
          <div
            className="crop-overlay"
            style={{
              left: crop.x,
              top: crop.y,
              width: crop.width,
              height: crop.height
            }}
          >
            <div className="crop-handle nw"></div>
            <div className="crop-handle ne"></div>
            <div className="crop-handle sw"></div>
            <div className="crop-handle se"></div>
          </div>
        </div>
        
        <div className="crop-info">
          <p>Crop size: {Math.round(crop.width)} × {Math.round(crop.height)} pixels</p>
          <p>Original size: {imageSize.width} × {imageSize.height} pixels</p>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
